import React, { useEffect, useState } from "react";
import {
  Home,
  Search,
  Bell,
  User,
  Plus,
  Upload,
  MapPin,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
  ChevronDown,
  Camera,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { setBureau } from "../../store/bureau.slice";
import {
  parseWebsiteLinks,
  getLoginWebsiteLinks,
  WebsiteLink,
} from "../../helpers/loginCredentials";
import { getRequest, putWithFormData } from "../../helpers/api.helper";
import { toast } from "react-toastify";
import Gnavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import { UPLOAD_URL } from "../../utils/constants";

type BureauFormData = {
  bureauName: string;
  ownerName: string;
  mobileNumber: string;
  websiteUrl: string;
  about: string;
  email: string;
  facebook: string;
  instagram: string;
  linkedin: string;
  youtube: string;
  twitter: string;
  profileImage: string | null;
};

type RawProfile = Partial<BureauFormData> & {
  bureauName?: string | null;
  image?: string | null;
  profile_img?: string | null;
};

const mapProfileToFormData = (profile?: RawProfile | null): BureauFormData => ({
  bureauName: profile?.bureauName ?? "",
  ownerName: profile?.ownerName ?? "",
  mobileNumber: profile?.mobileNumber ?? "",
  websiteUrl: (profile as any)?.websiteUrl ?? "",
  about: profile?.about ?? "",
  email: profile?.email ?? "",
  facebook: profile?.facebook ?? "",
  instagram: profile?.instagram ?? "",
  linkedin: profile?.linkedin ?? "",
  youtube: profile?.youtube ?? "",
  twitter: profile?.twitter ?? "",
  profileImage:
    profile?.profileImage ?? profile?.image ?? profile?.profile_img ?? "",
});

const getProfileImageUrl = (imagePath?: string | null) => {
  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }
  const cleaned = imagePath.replace(/^\/+/, "");
  const withoutUploads = cleaned.startsWith("uploads/")
    ? cleaned.replace(/^uploads\//, "")
    : cleaned;
  return `${UPLOAD_URL}/bureaus/profile_pic/${withoutUploads}`;
};

type WebsiteLinkErrors = Record<number, { heading?: string; url?: string }>;

const MAX_WEBSITE_LINKS = 19;

// Same rules as the backend: http(s) only, "example.com" -> "https://example.com".
const normalizeWebsiteLinkUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  const hasHttpScheme = /^https?:\/\//i.test(trimmed);
  if (!hasHttpScheme && /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)) return null;
  const candidate = hasHttpScheme ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(candidate);
    if (!["http:", "https:"].includes(parsed.protocol)) return null;
    if (parsed.username || parsed.password) return null;
    if (!parsed.hostname.includes(".")) return null;
  } catch {
    return null;
  }
  return candidate;
};

// Fully blank rows are dropped; partial or invalid rows are reported.
const validateWebsiteLinks = (links: WebsiteLink[]) => {
  const errors: WebsiteLinkErrors = {};
  const cleaned: WebsiteLink[] = [];
  links.forEach((link, index) => {
    const heading = link.heading.trim();
    const url = link.url.trim();
    if (!heading && !url) return;
    const rowErrors: { heading?: string; url?: string } = {};
    if (!heading) rowErrors.heading = "Heading Name is required.";
    const normalizedUrl = url ? normalizeWebsiteLinkUrl(url) : null;
    if (!url) rowErrors.url = "URL is required.";
    else if (!normalizedUrl)
      rowErrors.url = "Please enter a valid URL (e.g. https://example.com).";
    if (rowErrors.heading || rowErrors.url) {
      errors[index] = rowErrors;
      return;
    }
    cleaned.push({ heading, url: normalizedUrl as string });
  });
  return { cleaned, errors };
};

const EditMyProfile = () => {
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<BureauFormData>(
    mapProfileToFormData(bureauInfo),
  );
  const [websiteLinks, setWebsiteLinks] = useState<WebsiteLink[]>(
    getLoginWebsiteLinks(bureauInfo?.websiteUrl, bureauInfo?.websiteLinks).slice(1),
  );
  const [websiteHeading, setWebsiteHeading] = useState(
    getLoginWebsiteLinks(bureauInfo?.websiteUrl, bureauInfo?.websiteLinks)[0].heading,
  );
  const [websiteLinkErrors, setWebsiteLinkErrors] = useState<WebsiteLinkErrors>(
    {},
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [imageRemoved, setImageRemoved] = useState(false);

  useEffect(() => {
    const fetchMyProfile = async () => {
      try {
        const response = await getRequest(`/bureau/me`);
        if (response.success) {
          const profile = response.data?.profile ?? response.data;
          setFormData(mapProfileToFormData(profile));
          const links = getLoginWebsiteLinks(profile?.websiteUrl, profile?.websiteLinks);
          setWebsiteHeading(links[0].heading);
          setWebsiteLinks(links.slice(1));
          setImageRemoved(false);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMyProfile();
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!websiteHeading.trim()) {
      toast.error("Website Heading Name is required.");
      return;
    }
    const { cleaned: cleanedLinks, errors: linkErrors } =
      validateWebsiteLinks(websiteLinks);
    if (Object.keys(linkErrors).length > 0) {
      setWebsiteLinkErrors(linkErrors);
      toast.error("Please fix the highlighted Website Links.");
      return;
    }
    setWebsiteLinkErrors({});

    const formDataToSend = new FormData();
    (Object.keys(formData) as Array<keyof BureauFormData>).forEach((key) => {
      if (key === "profileImage") {
        return;
      }
      const value = formData[key] ?? "";
      formDataToSend.append(key, value);
    });
    const primaryLink = getLoginWebsiteLinks(formData.websiteUrl, [])[0];
    formDataToSend.append("websiteLinks", JSON.stringify([
      { ...primaryLink, heading: websiteHeading.trim() },
      ...cleanedLinks.filter((link) => link.url.replace(/\/+$/, "") !== primaryLink.url.replace(/\/+$/, "")),
    ]));
    if (selectedFile) {
      formDataToSend.append("image", selectedFile);
    } else if (imageRemoved) {
      formDataToSend.append("image", "");
    } else if (formData.profileImage) {
      formDataToSend.append("image", formData.profileImage);
    }
    try {
      // @ts-ignore
      const response = await putWithFormData(`/bureau/me`, formDataToSend);

      if (response.success) {
        toast("Bureau information updated successfully!");
        // Keep the shared bureau state current so later shares use the
        // saved links without a page reload.
        const updated = response.data ?? {};
        const savedLinks = parseWebsiteLinks(updated.websiteLinks);
        if (bureauInfo) {
          dispatch(
            setBureau({ ...bureauInfo, ...updated, websiteLinks: savedLinks }),
          );
        }
        setFormData(mapProfileToFormData(updated));
        const links = getLoginWebsiteLinks(updated.websiteUrl, savedLinks);
        setWebsiteHeading(links[0].heading);
        setWebsiteLinks(links.slice(1));
        setSelectedFile(null);
        setImageRemoved(false);
      } else {
        toast.error("Failed to update bureau information. " + response.message);
      }
    } catch (error) {
      toast.error("There was an error updating the bureau information.");
    }
  };

  const updateWebsiteLink = (
    index: number,
    field: keyof WebsiteLink,
    value: string,
  ) => {
    setWebsiteLinks((prev) =>
      prev.map((link, i) => (i === index ? { ...link, [field]: value } : link)),
    );
    setWebsiteLinkErrors((prev) =>
      prev[index]?.[field]
        ? { ...prev, [index]: { ...prev[index], [field]: undefined } }
        : prev,
    );
  };

  const addWebsiteLink = () => {
    setWebsiteLinks((prev) =>
      prev.length >= MAX_WEBSITE_LINKS ? prev : [...prev, { heading: "", url: "" }],
    );
  };

  const removeWebsiteLink = (index: number) => {
    setWebsiteLinks((prev) => prev.filter((_, i) => i !== index));
    setWebsiteLinkErrors({});
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files[0];

    // Optional: Add image size and type validation
    if (file) {
      const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validImageTypes.includes(file.type)) {
        alert("Please upload a valid image (JPEG, PNG, or GIF)");
        return;
      }

      if (file.size > maxSize) {
        alert("Image size should be less than 5MB");
        return;
      }

      setSelectedFile(file);
      setImageRemoved(false);
      event.target.value = "";
    }
  };

  const handleClearImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setFormData((prev) => ({
      ...prev,
      profileImage: "",
    }));
    setImageRemoved(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 pb-24">
      {/* Top Navigation */}
      <Gnavbar />

      {/* Main Content */}
      <form
        id="editProfileForm"
        onSubmit={handleSubmit}
        className="max-w-5xl mx-auto px-4 py-6 md:pb-6"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Edit Bureau Profile</h1>
            <p className="text-purple-100 text-sm">
              Update your bureau information and social links
            </p>
          </div>

          <div className="p-6 md:p-8 space-y-6">
            {/* Profile Image Upload */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-dashed border-purple-300">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-200 to-blue-200">
                    {previewUrl || formData.profileImage ? (
                      <img
                        src={
                          previewUrl ||
                          getProfileImageUrl(formData.profileImage)
                        }
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Camera size={40} className="text-purple-600" />
                    )}
                  </div>
                  <label className="absolute -bottom-3 -right-3 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-700 transition-colors shadow-lg">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    <Upload size={22} className="text-white" />
                  </label>
                </div>
                <p className="text-gray-700 font-medium mb-2">
                  {previewUrl || formData.profileImage
                    ? "Profile image"
                    : "No profile image uploaded yet"}
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  Upload your bureau logo or profile picture
                </p>
                <div className="flex items-center gap-3">
                  {(previewUrl || formData.profileImage) && (
                    <button
                      type="button"
                      onClick={handleClearImage}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove image
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bureau Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Bureau Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.bureauName}
                  onChange={(e) =>
                    setFormData({ ...formData, bureauName: e.target.value })
                  }
                  placeholder="Enter your bureau name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Bureau Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Owner Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.ownerName}
                  onChange={(e) =>
                    setFormData({ ...formData, ownerName: e.target.value })
                  }
                  placeholder="Enter your owner name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Mobile Number */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, mobileNumber: e.target.value })
                  }
                  placeholder="Enter mobile number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Website URL */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Website Heading Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={websiteHeading}
                  onChange={(e) => setWebsiteHeading(e.target.value)}
                  placeholder="Enter Heading Name"
                  maxLength={100}
                  required
                  className="mb-3 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Website Link / URL
                </label>
                <input
                  type="url"
                  value={formData.websiteUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, websiteUrl: e.target.value })
                  }
                  placeholder="https://your-website.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Website Links */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Website Links
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  These links are added when you share profile login details.
                </p>
                <div className="space-y-4">
                  {websiteLinks.map((link, index) => {
                    const errors = websiteLinkErrors[index];
                    return (
                      <div
                        key={index}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
                      >
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Heading Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={link.heading}
                            onChange={(e) =>
                              updateWebsiteLink(index, "heading", e.target.value)
                            }
                            placeholder="Enter Heading Name"
                            maxLength={100}
                            className={`w-full px-4 py-3 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                              errors?.heading ? "border-red-400" : "border-gray-300"
                            }`}
                          />
                          {errors?.heading && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.heading}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            URL <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="url"
                            value={link.url}
                            onChange={(e) =>
                              updateWebsiteLink(index, "url", e.target.value)
                            }
                            placeholder="Enter URL"
                            className={`w-full px-4 py-3 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                              errors?.url ? "border-red-400" : "border-gray-300"
                            }`}
                          />
                          {errors?.url && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.url}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeWebsiteLink(index)}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={addWebsiteLink}
                    disabled={websiteLinks.length >= MAX_WEBSITE_LINKS}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-300 text-purple-700 font-medium hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                    Add More URL
                  </button>
                </div>
              </div>

              {/* About */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  About
                </label>
                <textarea
                  value={formData.about}
                  onChange={(e) =>
                    setFormData({ ...formData, about: e.target.value })
                  }
                  placeholder="Tell us about your bureau..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="Enter your email"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Update Button - Desktop */}
            <div className="pt-6 hidden md:block">
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
              >
                Update Profile
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Fixed Update Button - Mobile */}
      <div className="md:hidden bottom-16 left-0 right-0 p-4 bg-white border-t shadow-lg z-40">
        <button
          type="submit"
          form="editProfileForm"
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
        >
          Update Profile
        </button>
      </div>

      {/* Bottom Navigation - Mobile */}
      <BottomNavbar />
    </div>
  );
};

export default EditMyProfile;
